import { useQuery } from '@tanstack/react-query';
import {
  DISPATCH_QUEUES,
  whyNotReschedulable,
  type DispatchQueue,
  type DispatchQueueRow,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import {
  UnderlineTabs,
  UnderlineTabList,
  UnderlineTab,
  UnderlineTabPanel,
} from '../ui/Tabs';
import { DataTable, dash, type DataTableColumn } from '@/components/ui/data-table';
import { StatusBadge } from '../ui/Badge';
import { JOB_STATUS, VISIT_STATUS, display, requestStatus } from '../status';
import { ErrorState } from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { IconSchedule } from '../icons';
import { dayInTz, timeInTz } from '../../lib/format';
import type { PreviewTarget } from './JobPreviewPanel';

/**
 * The dispatch workspace's bottom pane — four queues of work the board above cannot show
 * (114 part 5, design 113).
 *
 * ⭐ **These are not filters over the day on the board.** Each queue holds work that is
 * structurally absent from every day board: a job with no visit is on no day at all; a held
 * job is off the schedule by definition; a request is not a job yet. The one exception —
 * unassigned visits — is on exactly one day's board and nowhere else, so an unassigned visit
 * from last Tuesday is invisible unless you happen to be looking at last Tuesday.
 *
 * ⚠️ **A fifth tab, "Partially completed", is missing on purpose.** Its approved definition
 * (113/A4b) is *every checklist item done AND the customer signed off*.
 *
 * 🟢 **Built 2026-08-27.** Customer sign-off landed with ticket 118 (`job_signoffs`), so the
 * tab is now built on BOTH halves rather than the checklist half under the stronger name.
 * The queue is scoped to jobs where work has begun — see the note on `DISPATCH_QUEUES`.
 */
const LABEL: Record<DispatchQueue, string> = {
  unscheduled: 'Unscheduled',
  unassigned: 'Unassigned visits',
  on_hold: 'On hold',
  partially_completed: 'Partially completed',
  requests: 'Requests',
};

/**
 * What each queue's date column MEANS.
 *
 * ⚠️ It differs per queue and the header has to say which, because the same column holds
 * three different facts. "On hold" is the one worth reading twice: nothing in the schema
 * records WHEN a job was paused, so the column is `updated_at` and is labelled "Updated"
 * rather than "On hold since" — which would be true right up until somebody edits the title
 * of a job that has been paused for a month.
 */
const WHEN: Record<DispatchQueue, string> = {
  unscheduled: 'Created',
  unassigned: 'Scheduled',
  on_hold: 'Updated',
  // Same column and same caveat as `on_hold`: nothing records WHEN the work stalled.
  partially_completed: 'Updated',
  requests: 'Received',
};

/** The empty state, in the words that say what clearing the queue would mean. */
const EMPTY: Record<DispatchQueue, string> = {
  unscheduled: 'Every job has a visit booked.',
  unassigned: 'Every booked visit has somebody on it.',
  on_hold: 'Nothing is paused.',
  // Names the STRONGER definition, so an empty tab cannot be read as "the checklists are done".
  partially_completed: 'Every job under way is ticked off and signed for.',
  requests: 'No new requests waiting on triage.',
};

/**
 * When the row happened, at the SHOP.
 *
 * ⚠️ Only the unassigned queue gets a clock. Its rows are booked visits, where the hour is
 * the whole point of being unassigned; the other three are dated facts (created, updated,
 * received) and a time of day on them is noise that pushes the address out of the column.
 */
const whenLabel = (queue: DispatchQueue, at: Date | string, tz: string) =>
  queue === 'unassigned' ? `${dayInTz(at, tz)} · ${timeInTz(at, tz)}` : dayInTz(at, tz);

const statusOf = (queue: DispatchQueue, status: string) =>
  queue === 'requests'
    ? requestStatus(status)
    : queue === 'unassigned'
      ? display(VISIT_STATUS, status)
      : display(JOB_STATUS, status);

/**
 * A queue row as the panel's target — the THIRD of A5's three openers.
 *
 * ⚠️ `visitId` is null for three of the four queues, and the panel is built for that: an
 * unscheduled job has no visit to show a time for or to move, and saying "Not scheduled yet"
 * is the whole reason the row is in that queue.
 */
export function queueRowTarget(
  queue: DispatchQueue,
  row: DispatchQueueRow,
): PreviewTarget {
  return {
    key: row.key,
    jobId: row.jobId,
    visitId: row.visitId,
    kind: queue === 'requests' ? 'request' : row.visitId ? 'visit' : 'job',
    ref: row.ref,
    title: row.title,
    status: row.status,
    customer: row.customer,
    // The queue's projection carries no phone, so the row is left out (`undefined`, "not
    // carried") rather than dashed; the panel's footer opens the job, which has it. Nor does it
    // carry the property's label, which simply has no row when null.
    phone: undefined,
    propertyLabel: null,
    address: row.address,
    assignees: [],
    startAt: queue === 'unassigned' ? row.at : null,
    endAt: null,
    /**
     * ⚠️ Asked of the SAME predicate the board card asks, not left null.
     *
     * On the unassigned queue `row.status` IS the visit's status, so a visit whose tech is
     * already en route would otherwise get two move buttons the server is guaranteed to
     * refuse — the "button that can only fail" shape this codebase greys out everywhere
     * else. `whyNotReschedulable` lives in shared for exactly this: one rule, both ends.
     */
    stuck: row.visitId ? whyNotReschedulable(row.status) : null,
  };
}

export function JobQueues({
  queue,
  onQueueChange,
  tz,
  onOpen,
}: {
  queue: DispatchQueue;
  onQueueChange: (next: DispatchQueue) => void;
  tz: string;
  onOpen: (target: PreviewTarget) => void;
}) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.dispatch.queues.queryOptions({ queue, limit: 25 }),
    // Switching tabs must not blank the strip: every call returns ALL FOUR counts, so the
    // previous answer's numbers are still the right numbers while the new rows load.
    placeholderData: (prev) => prev,
  });

  const columns: DataTableColumn<DispatchQueueRow>[] = [
    {
      key: 'ref',
      label: 'Ref',
      value: (r) => r.ref,
      cell: (r) => <span className="tabular-nums">{r.ref}</span>,
    },
    { key: 'title', label: 'Job', value: (r) => r.title, cell: (r) => r.title },
    {
      key: 'customer',
      label: 'Customer',
      value: (r) => r.customer,
      cell: (r) => r.customer,
    },
    {
      key: 'address',
      label: 'Address',
      value: (r) => r.address,
      cell: (r) => dash(r.address),
    },
    {
      key: 'status',
      label: 'Status',
      value: (r) => statusOf(queue, r.status).label,
      cell: (r) => {
        const s = statusOf(queue, r.status);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'at',
      label: WHEN[queue],
      // ⚠️ The sort key is the epoch, the search key is the rendered text. Sorting on the
      // formatted string puts "April" before "January", and searching the epoch lets "2026"
      // match a number nobody can see — the two are different jobs and the table wants both.
      value: (r) => (r.at ? new Date(r.at).getTime() : null),
      searchValue: (r) => (r.at ? whenLabel(queue, r.at, tz) : null),
      cell: (r) => (r.at ? whenLabel(queue, r.at, tz) : dash(null)),
    },
  ];

  return (
    <UnderlineTabs value={queue} onValueChange={(v) => onQueueChange(v as DispatchQueue)}>
      <UnderlineTabList>
        {DISPATCH_QUEUES.map((k) => (
          <UnderlineTab key={k} value={k}>
            <span className="flex items-center gap-2">
              {LABEL[k]}
              {/* The count comes from the SERVER on every call, never from `rows.length` —
                  the rows are capped at 25 and a count read off them would be short by
                  exactly the number the cap hid. */}
              {q.data ? (
                <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
                  {q.data.counts[k]}
                </span>
              ) : null}
            </span>
          </UnderlineTab>
        ))}
      </UnderlineTabList>

      {DISPATCH_QUEUES.map((k) => (
        <UnderlineTabPanel key={k} value={k}>
          {k !== queue ? null : q.isError ? (
            <ErrorState
              icon={IconSchedule}
              what="these queues"
              onRetry={() => void q.refetch()}
            />
          ) : !q.data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={q.data.rows}
                rowKey={(r) => r.key}
                rowLabel={(r) => `${r.ref} ${r.title}`}
                onRowClick={(r) => onOpen(queueRowTarget(queue, r))}
                empty={
                  <EmptyState
                    icon={IconSchedule}
                    title={`Nothing in ${LABEL[queue].toLowerCase()}`}
                    description={EMPTY[queue]}
                  />
                }
              />
              {q.data.more > 0 ? (
                /* ⚠️ A capped list MUST state its total. Without this line the pane reads
                   as the whole queue, and the tab's own count beside it would look wrong
                   rather than looking like what it is: the truth. */
                <p className="px-1 pt-2 text-xs text-muted-foreground">
                  Showing the {q.data.rows.length} oldest of {q.data.counts[queue]} —{' '}
                  {q.data.more} more not listed.
                </p>
              ) : null}
            </>
          )}
        </UnderlineTabPanel>
      ))}
    </UnderlineTabs>
  );
}
